import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    IconButton,
    TextField,
    InputAdornment,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    CardMedia,
    CardContent,
    CardActionArea,
    Tabs,
    Tab,
    LinearProgress
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterListIcon,
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    InsertDriveFile as FileIcon,
    GridView as GridViewIcon,
    ViewList as ListViewIcon,
    Close as CloseIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';
import api from '../../../services/api';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import LottieLoading from '../../../components/common/LottieLoading';
import lmsLoadingAnimation from '../../../assets/animations/lms-loading.json'; // Closest fallback


// Types
interface MediaFile {
    _id: string;
    filename: string;
    path: string;
    mimetype: string;
    size: number;
    url: string;
    thumbnailUrl?: string | null;
    alt?: string;
    createdAt: string;
}

const MediaLibraryPage = () => {
    const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
    const [filteredFiles, setFilteredFiles] = useState<MediaFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [filterType, setFilterType] = useState<string>('all');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const [totalFiles, setTotalFiles] = useState(0);

    // Details Modal
    const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [editAlt, setEditAlt] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Context
    const { showToast } = useToast();
    const { user } = useAuth();
    const [dragActive, setDragActive] = useState(false);

    // Fetch Media
    const fetchMedia = async (pageNum = 1, append = false) => {
        try {
            if (pageNum === 1) setLoading(true);

            const res = await api.get(`/api/media?page=${pageNum}&limit=20`);
            const newFiles = res.data.data;
            const pagination = res.data.pagination;

            setMediaFiles(prev => append ? [...prev, ...newFiles] : newFiles);
            setFilteredFiles(prev => append ? [...prev, ...newFiles] : newFiles); // Initialize filtered with all loaded so far
            setHasMore(pagination.page < pagination.pages);
            setTotalFiles(pagination.total);

        } catch (err) {
            console.error(err);
            showToast('Erreur lors du chargement de la médiathèque', 'error');
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    };

    useEffect(() => {
        fetchMedia(1, false);
    }, []);

    // Load More Handler (Infinite Scroll)
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        // Use a threshold (e.g., 50px) because exact equality is unreliable with zoom/high-DPI
        if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !loading) {
            setPage(prev => {
                const nextPage = prev + 1;
                fetchMedia(nextPage, true);
                return nextPage;
            });
        }
    };

    // Filter Logic - Client side filtering on loaded items
    useEffect(() => {
        let result = mediaFiles;

        if (searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            result = result.filter(file => file.filename.toLowerCase().includes(lowerQuery));
        }

        if (filterType !== 'all') {
            result = result.filter(file => file.mimetype.startsWith(filterType));
        }

        setFilteredFiles(result);
    }, [searchQuery, filterType, mediaFiles]);

    // Format Bytes
    const formatBytes = (bytes: number, decimals = 2) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    // Upload Handling
    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileUpload(e.dataTransfer.files[0]);
        }
    }, []);

    const handleFileUpload = async (file: File) => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setUploading(true);
            setUploadProgress(0);

            await api.post('/api/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-type' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
                    setUploadProgress(percentCompleted);
                }
            });

            showToast('Fichier téléchargé avec succès', 'success');
            showToast('Fichier téléchargé avec succès', 'success');
            setPage(1);
            fetchMedia(1, false);
        } catch (err) {
            console.error(err);
            showToast('Erreur lors du téléchargement', 'error');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Delete Handling
    const handleDelete = async (id: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fichier ?')) return;

        try {
            await api.delete(`/api/media/${id}`);
            showToast('Fichier supprimé', 'success');
            setMediaFiles(prev => prev.filter(f => f._id !== id));
            if (selectedMedia?._id === id) {
                setDetailsOpen(false);
                setSelectedMedia(null);
            }
        } catch (err) {
            console.error(err);
            showToast('Erreur lors de la suppression', 'error');
        }
    };

    const handleCopyUrl = (url: string) => {
        navigator.clipboard.writeText(url);
        showToast('Lien copié dans le presse-papier', 'success');
    };

    const handleUpdateAlt = async () => {
        if (!selectedMedia) return;
        try {
            setIsSaving(true);
            const res = await api.put(`/api/media/${selectedMedia._id}`, { alt: editAlt });

            // Update local state
            const updatedFile = res.data;
            setMediaFiles(prev => prev.map(f => f._id === updatedFile._id ? { ...f, alt: updatedFile.alt } : f));
            setSelectedMedia(prev => prev ? { ...prev, alt: updatedFile.alt } : null);

            showToast('Texte alternatif mis à jour', 'success');
        } catch (err) {
            console.error(err);
            showToast('Erreur lors de la mise à jour', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const openDetails = (file: MediaFile) => {
        setSelectedMedia(file);
        setEditAlt(file.alt || '');
        setDetailsOpen(true);
    };

    if (initialLoad) {
        return <LottieLoading message="Chargement de la médiathèque..." />;
    }

    return (
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Header & Controls */}
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        Médiathèque
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {filteredFiles.length} affichés sur {totalFiles} fichiers ({formatBytes(mediaFiles.reduce((acc, curr) => acc + curr.size, 0))})
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, justifyContent: 'flex-end' }}>
                    <TextField
                        size="small"
                        placeholder="Rechercher..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        }}
                        sx={{ width: 300 }}
                    />

                    <Box sx={{ display: 'flex', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Button
                            color={filterType === 'all' ? 'primary' : 'inherit'}
                            onClick={() => setFilterType('all')}
                            sx={{ minWidth: 'auto', px: 2 }}
                        >
                            Tout
                        </Button>
                        <Button
                            color={filterType === 'image' ? 'primary' : 'inherit'}
                            onClick={() => setFilterType('image')}
                            sx={{ minWidth: 'auto', px: 2 }}
                        >
                            Images
                        </Button>
                        <Button
                            color={filterType === 'video' ? 'primary' : 'inherit'}
                            onClick={() => setFilterType('video')}
                            sx={{ minWidth: 'auto', px: 2 }}
                        >
                            Vidéos
                        </Button>
                    </Box>

                    <IconButton onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')} color="primary">
                        {viewMode === 'grid' ? <ListViewIcon /> : <GridViewIcon />}
                    </IconButton>

                    <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        component="label"
                        sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)', color: 'white' }}
                    >
                        Importer
                        <input type="file" hidden onChange={(e) => e.target.files && handleFileUpload(e.target.files[0])} />
                    </Button>
                </Box>
            </Paper>

            {/* Upload Progress */}
            {uploading && (
                <Box sx={{ width: '100%' }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="caption" align="center" display="block">Téléchargement en cours... {uploadProgress}%</Typography>
                </Box>
            )}

            {/* Drop Zone (if dragging) */}
            {dragActive && (
                <Box
                    sx={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        bgcolor: 'rgba(33, 150, 243, 0.1)',
                        zIndex: 99,
                        border: '3px dashed #2196F3',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    <Typography variant="h4" color="primary">Déposez vos fichiers ici</Typography>
                </Box>
            )}


            {/* Scroll Container for Infinite Loading */}
            <Box
                sx={{ flexGrow: 1, overflow: 'auto', position: 'relative', pb: 10 }}
                onScroll={handleScroll}
                onDragEnter={handleDrag}
            >
                {viewMode === 'grid' ? (
                    <Grid container spacing={2}>
                        {filteredFiles.map((file) => (
                            <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={file._id}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex', flexDirection: 'column',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'scale(1.02)', boxShadow: 6 }
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => openDetails(file)}
                                        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: 200 }}
                                    >
                                        {file.mimetype.startsWith('image') ? (
                                            <CardMedia
                                                component="img"
                                                height="140"
                                                image={file.thumbnailUrl || file.url}
                                                alt={file.filename}
                                                sx={{ objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', position: 'relative' }}>
                                                {file.thumbnailUrl ? (
                                                    <img src={file.thumbnailUrl} alt={file.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <FileIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                                                )}
                                            </Box>
                                        )}
                                        <CardContent sx={{ flexGrow: 1, p: 1 }}>
                                            <Typography variant="caption" title={file.filename} noWrap display="block" fontWeight="bold">
                                                {file.filename}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {formatBytes(file.size)}
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
                                        <IconButton size="small" onClick={() => handleCopyUrl(file.url)} title="Copier le lien">
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={(e) => handleDelete(file._id, e)} color="error" title="Supprimer">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Paper>
                        {/* List View Implementation could be a DataGrid or Simple Table */}
                        <Box sx={{ p: 2 }}>
                            {filteredFiles.map((file) => (
                                <Box
                                    key={file._id}
                                    sx={{
                                        display: 'flex', alignItems: 'center', p: 1,
                                        borderBottom: '1px solid #f0f0f0',
                                        '&:hover': { bgcolor: '#f9f9f9' }
                                    }}
                                >
                                    <Box sx={{ width: 40, height: 40, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eee', borderRadius: 1, overflow: 'hidden' }}>
                                        {file.thumbnailUrl || file.mimetype.startsWith('image') ? (
                                            <img src={file.thumbnailUrl || file.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <FileIcon />
                                        )}
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="body2" fontWeight="bold">{file.filename}</Typography>
                                        <Typography variant="caption" color="text.secondary">{file.mimetype} • {formatBytes(file.size)}</Typography>
                                    </Box>
                                    <IconButton size="small" onClick={() => handleCopyUrl(file.url)}><CopyIcon /></IconButton>
                                    <IconButton size="small" onClick={() => handleDelete(file._id)} color="error"><DeleteIcon /></IconButton>
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                )}

                {loading && !initialLoad && (
                    <Box sx={{ width: '100%', mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <LinearProgress sx={{ width: '50%' }} />
                    </Box>
                )}
            </Box>

            {!loading && hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, pb: 15 }}>
                    <Button variant="outlined" onClick={() => {
                        setPage(prev => {
                            const nextPage = prev + 1;
                            fetchMedia(nextPage, true);
                            return nextPage;
                        });
                    }}>
                        Charger plus
                    </Button>
                </Box>
            )}

            {loading && !initialLoad && (
                <Box sx={{ width: '100%', mt: 2, display: 'flex', justifyContent: 'center', pb: 15 }}>
                    <LinearProgress sx={{ width: '50%' }} />
                </Box>
            )}

            {/* Details Dialog */}
            <Dialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                maxWidth="md"
                fullWidth
            >
                {selectedMedia && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            Détails du fichier
                            <IconButton onClick={() => setDetailsOpen(false)}><CloseIcon /></IconButton>
                        </DialogTitle>
                        <DialogContent>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f0f0', borderRadius: 2, minHeight: 300 }}>
                                    {selectedMedia.mimetype.startsWith('image') ? (
                                        <img src={selectedMedia.url} alt={selectedMedia.filename} style={{ maxWidth: '100%', maxHeight: '400px' }} />
                                    ) : selectedMedia.mimetype.startsWith('video') ? (
                                        <video src={selectedMedia.url} controls style={{ maxWidth: '100%', maxHeight: '400px' }} />
                                    ) : (
                                        <Box sx={{ textAlign: 'center' }}>
                                            <FileIcon sx={{ fontSize: 100, color: 'text.secondary' }} />
                                            <Typography>Aperçu non disponible</Typography>
                                        </Box>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Typography variant="subtitle2" color="text.secondary">Nom du fichier</Typography>
                                    <Typography variant="body1" paragraph>{selectedMedia.filename}</Typography>

                                    <Typography variant="subtitle2" color="text.secondary">Texte alternatif (Alt)</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={editAlt}
                                            onChange={(e) => setEditAlt(e.target.value)}
                                            placeholder="Description pour l'accessibilité"
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleUpdateAlt}
                                            disabled={isSaving}
                                            size="small"
                                        >
                                            Enregistrer
                                        </Button>
                                    </Box>


                                    <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                                    <Typography variant="body1" paragraph>{selectedMedia.mimetype}</Typography>

                                    <Typography variant="subtitle2" color="text.secondary">Taille</Typography>
                                    <Typography variant="body1" paragraph>{formatBytes(selectedMedia.size)}</Typography>

                                    <Typography variant="subtitle2" color="text.secondary">URL</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f5f5f5', p: 1, borderRadius: 1, mb: 2 }}>
                                        <Typography variant="caption" noWrap sx={{ flexGrow: 1 }}>{selectedMedia.url}</Typography>
                                        <IconButton size="small" onClick={() => handleCopyUrl(selectedMedia.url)}><CopyIcon fontSize="small" /></IconButton>
                                    </Box>

                                    <Button
                                        variant="outlined"
                                        color="error"
                                        fullWidth
                                        startIcon={<DeleteIcon />}
                                        onClick={() => handleDelete(selectedMedia._id)}
                                    >
                                        Supprimer
                                    </Button>
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </Box >
    );
};

export default MediaLibraryPage;
